/* eslint-disable react/require-default-props */
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import SignaturePad from 'react-signature-canvas';
import { Button, FlexColumn, Typography } from 'design-system';
import { PencilIcon } from 'design-system/components/Icons';

const SIGNATURE_WIDTH = 400;
const SIGNATURE_HEIGHT = 150;

function SignaturePlaceholder({ preview, signed }) {
  return (
    <FlexColumn
      align="center"
      justify="center"
      fullWidth
      className={[
        'cc-preview-signature',
        preview ? '' : 'mt-2',
        signed ? 'cc-preview-signature--signed' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-hidden
    >
      <PencilIcon size={24} className="ds-icon--color-muted" aria-hidden />
      <Typography component="span" variant="body2" className="cc-preview-signature__label">
        {signed ? 'Signed' : 'Sign here'}
      </Typography>
    </FlexColumn>
  );
}

SignaturePlaceholder.propTypes = {
  preview: PropTypes.bool,
  signed: PropTypes.bool,
};

export default function SignatureTypeAnswer({
  preview = false,
  value,
  onChange,
  interactive = false,
}) {
  const intl = useIntl();
  const signatureRef = useRef(null);
  const signed = Boolean(value);

  useEffect(() => {
    if (!interactive || !signatureRef.current) {
      return;
    }
    if (!value) {
      signatureRef.current.clear();
      return;
    }
    try {
      if (signatureRef.current.isEmpty()) {
        signatureRef.current.fromDataURL(value, {
          width: SIGNATURE_WIDTH,
          height: SIGNATURE_HEIGHT,
        });
      }
    } catch (e) {
      signatureRef.current.clear();
    }
  }, [interactive, value]);

  if (!interactive) {
    return <SignaturePlaceholder preview={preview} signed={signed} />;
  }

  const handleSignatureEnd = () => {
    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      return;
    }
    onChange?.(signatureRef.current.toDataURL());
  };

  const handleClear = () => {
    signatureRef.current?.clear();
    onChange?.('');
  };

  return (
    <FlexColumn align="stretch" fullWidth className="cc-preview-signature-pad mt-2">
      <div className="cc-preview-signature-pad__canvas-wrap">
        <SignaturePad
          ref={signatureRef}
          canvasProps={{
            width: SIGNATURE_WIDTH,
            height: SIGNATURE_HEIGHT,
            className: 'cc-preview-signature-pad__canvas',
          }}
          onEnd={handleSignatureEnd}
        />
        <Button
          type="button"
          variant="link"
          size="sm"
          className="cc-preview-signature-pad__clear"
          onClick={handleClear}
        >
          {intl.formatMessage({ id: 'clear' })}
        </Button>
      </div>
    </FlexColumn>
  );
}

SignatureTypeAnswer.propTypes = {
  preview: PropTypes.bool,
  value: PropTypes.string,
  onChange: PropTypes.func,
  interactive: PropTypes.bool,
};
